import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { apiClient } from '../utils/apiClient'
import { Project } from '../types'

interface ProjectContextType {
  // Legacy localStorage fields (used by Repositories.tsx)
  selectedProjectId: string | null
  defaultProjectId: string | null
  setSelectedProjectId: (projectId: string | null) => void
  setDefaultProjectId: (projectId: string | null) => void
  // Multi-repo Projects list
  projects: Project[]
  projectsLoading: boolean
  projectsError: string | null
  refetchProjects: () => Promise<void>
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined)

export const useProject = () => {
  const context = useContext(ProjectContext)
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider')
  }
  return context
}

export const useProjects = () => {
  const context = useContext(ProjectContext)
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider')
  }
  const { projects, projectsLoading, projectsError, refetchProjects } = context
  return { projects, projectsLoading, projectsError, refetchProjects }
}

interface ProjectProviderProps {
  children: ReactNode
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const [selectedProjectId, setSelectedProjectIdState] = useState<string | null>(() => {
    return localStorage.getItem('selectedProjectId')
  })

  const [defaultProjectId, setDefaultProjectIdState] = useState<string | null>(() => {
    return localStorage.getItem('defaultProjectId')
  })

  const [projects, setProjects] = useState<Project[]>([])
  const [projectsLoading, setProjectsLoading] = useState(false)
  const [projectsError, setProjectsError] = useState<string | null>(null)
  const [hasFetched, setHasFetched] = useState(false)
  const { user } = useAuth()

  const setSelectedProjectId = (projectId: string | null) => {
    setSelectedProjectIdState(projectId)
    if (projectId) {
      localStorage.setItem('selectedProjectId', projectId)
    } else {
      localStorage.removeItem('selectedProjectId')
    }
  }

  const setDefaultProjectId = (projectId: string | null) => {
    setDefaultProjectIdState(projectId)
    if (projectId) {
      localStorage.setItem('defaultProjectId', projectId)
    } else {
      localStorage.removeItem('defaultProjectId')
    }
  }

  const fetchProjects = async (force = false) => {
    if (!user || (hasFetched && !force)) return
    try {
      setProjectsLoading(true)
      setProjectsError(null)
      const data = await apiClient.get('/api/projects')
      setProjects(Array.isArray(data) ? data : [])
      setHasFetched(true)
    } catch {
      setProjects([])
      setProjectsError(null) // Silently fail — backend not yet implemented
    } finally {
      setProjectsLoading(false)
    }
  }

  const refetchProjects = async () => {
    await fetchProjects(true)
  }

  useEffect(() => {
    if (user && !hasFetched) {
      fetchProjects()
    } else if (!user) {
      setProjects([])
      setHasFetched(false)
      setProjectsError(null)
    }
  }, [user, hasFetched])

  const value: ProjectContextType = {
    selectedProjectId,
    defaultProjectId,
    setSelectedProjectId,
    setDefaultProjectId,
    projects,
    projectsLoading,
    projectsError,
    refetchProjects,
  }

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  )
}
